/**
 * Invite page panels.
 *
 * Each exported panel is one of the shapes `/me/invite` can take. They are pure
 * presentation: the page decides which one fits the status the server reported,
 * and every panel receives already-translated text or a callback.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { InviteApplicationDto } from '@shared/types'

import { currentLocale } from '../../lib/i18n'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { formatUnixSeconds } from '../../lib/format'

/** Account is activated, or is an administrator and needs no code. */
export function InvitedPanel({ canCreateEvent }: { canCreateEvent: boolean }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-4 sm:pt-5">
        <Alert variant="success">
          <AlertTitle>{t('invite.invited.title')}</AlertTitle>
          <AlertDescription>{t('invite.invited.body')}</AlertDescription>
        </Alert>
        {canCreateEvent ? (
          <Button asChild className="self-start">
            <Link to="/me/events/new/title">{t('invite.invited.goCreate')}</Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

/** A request is waiting for an administrator. */
export function PendingPanel({
  application,
  onWithdraw,
  isWithdrawing,
}: {
  application: InviteApplicationDto
  onWithdraw: () => void
  isWithdrawing: boolean
}) {
  const { t } = useTranslation()
  const locale = currentLocale()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invite.pending.title')}</CardTitle>
        <CardDescription>{t('invite.pending.body')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="flex flex-col gap-2 text-sm">
          <div className="flex flex-wrap gap-2">
            <dt className="text-muted-foreground">{t('invite.pending.submittedAt')}</dt>
            <dd>{formatUnixSeconds(application.createdAt, locale)}</dd>
          </div>
          {application.reason ? (
            <div className="flex flex-col gap-1">
              <dt className="text-muted-foreground">{t('invite.pending.reasonLabel')}</dt>
              <dd className="whitespace-pre-wrap">{application.reason}</dd>
            </div>
          ) : null}
        </dl>
        <Button
          variant="outline"
          className="self-start"
          disabled={isWithdrawing}
          onClick={onWithdraw}
        >
          {isWithdrawing ? t('invite.pending.withdrawing') : t('invite.pending.withdraw')}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Last request was rejected and the cooldown has not elapsed.
 *
 * The decision note is the rejection reason the applicant is owed: it is shown
 * verbatim, together with the moment applying becomes possible again.
 */
export function CooldownPanel({
  application,
  cooldownEndsAt,
}: {
  application: InviteApplicationDto
  cooldownEndsAt: number
}) {
  const { t } = useTranslation()
  const locale = currentLocale()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invite.cooldown.title')}</CardTitle>
        <CardDescription>{t('invite.cooldown.body')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <RejectionReason application={application} />
        <p className="text-sm">
          {t('invite.cooldown.retryAt', { date: formatUnixSeconds(cooldownEndsAt, locale) })}
        </p>
      </CardContent>
    </Card>
  )
}

/** The request entry point is switched off deployment wide. */
export function ClosedPanel() {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invite.closed.title')}</CardTitle>
        <CardDescription>{t('invite.closed.body')}</CardDescription>
      </CardHeader>
    </Card>
  )
}

/**
 * The most recent rejection, when one exists.
 *
 * Shown above the application form too: a user whose cooldown expired should
 * still see why the previous request was turned down.
 */
export function RejectionReason({ application }: { application: InviteApplicationDto }) {
  const { t } = useTranslation()

  if (application.status !== 'rejected') {
    return null
  }

  return (
    <Alert variant="warning">
      <AlertTitle>{t('invite.rejectedTitle')}</AlertTitle>
      <AlertDescription>
        {application.decisionNote ?? t('invite.rejectedNoReason')}
      </AlertDescription>
    </Alert>
  )
}
